import { toast } from "@/components/ui";
import { useVoidGenericObservation } from "@/features/Charting/network/useVoidGenericObservation";
import { formatDate, formatDateTime } from "@/lib/time";
import { useGetGenericObservationHistory } from "@/network/useGenericObservations";
import type { ObservationGroupItem } from "@/types/observations";
import React from "react";
import { HistoryModal, type HistoryRow } from "./HistoryModal";
import type { GenericObservationDisplayProps } from "./types";

type DisplayComponentType = React.ComponentType<GenericObservationDisplayProps>;

export interface GenericObservationHistoryModalProps {
  displayComponent: DisplayComponentType;
  group: string;
  observationCode: string;
  observationCodeFriendlyDisplay: string;
  observationCodeSystem: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patientId: string;
}

function getInfoItems(
  observation: ObservationGroupItem["documentedObservation"],
) {
  const performedByPatient = observation?.performer?.[0]?.type === "Patient";
  return [
    `Performed by ${observation?.performer?.[0]?.display} ${performedByPatient ? "(Patient)" : ""} on ${formatDateTime(observation?.effectiveDateTime)}`,
  ];
}

export const GenericObservationHistoryModal = ({
  displayComponent: DisplayComponent,
  group,
  observationCode,
  observationCodeFriendlyDisplay,
  observationCodeSystem,
  onOpenChange,
  open,
  patientId,
}: GenericObservationHistoryModalProps) => {
  const { data, isLoading, isError } = useGetGenericObservationHistory({
    code: observationCode,
    enabled: open,
    group,
    patientId,
    system: observationCodeSystem,
  });

  const { mutate: voidObservation, isPending: isVoidingObservation } =
    useVoidGenericObservation({
      patientId,
      encounterId: undefined, // Not needed for history modal
      group,
      observationCode,
      onSuccess: () => {
        toast.success("Observation voided successfully");
      },
    });

  const rows: HistoryRow[] = React.useMemo(() => {
    const items = data?.observations ?? [];
    return items.map((item) => {
      // DisplayComponent requires a minimal ObservationGroupItem-like object (only uses documentedObservation)
      const displayItem: Partial<ObservationGroupItem> = {
        documentedObservation: item.observation,
      };

      return {
        id: item.observation?.id,
        date: formatDate(item.observationDate),
        result: <DisplayComponent item={displayItem as ObservationGroupItem} />,
        infoItems: getInfoItems(item.observation),
        onVoid: item.observation
          ? () => {
              voidObservation({
                observationId: item.observation?.id as string,
              });
            }
          : undefined,
        isVoidPending: isVoidingObservation,
      };
    });
  }, [data, DisplayComponent, voidObservation, isVoidingObservation]);

  return (
    <HistoryModal
      emptyMessage="No observation history found."
      title={observationCodeFriendlyDisplay}
      rows={rows}
      onOpenChange={onOpenChange}
      open={open}
      isLoading={isLoading}
      isError={isError}
    />
  );
};
